import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export type PayrollPeriodStatus = 'DRAFT' | 'PREPARED' | 'SENT' | 'PAYSLIPS_RECEIVED' | 'CLOSED';
export type PayrollTimesheetStatus = 'DRAFT' | 'READY' | 'SENT' | 'ERROR';

export interface PayrollPeriod {
  id: number;
  period: string; // yyyy-MM
  agencyId?: number | null;
  status: PayrollPeriodStatus;
  creationDate?: string;
  updateDate?: string | null;
}

export interface PayrollDayRecord {
  date: string;       // yyyy-MM-dd
  startTime: string | null; // HH:mm
  endTime: string | null;   // HH:mm
  pauseMinutes?: number | null;
  hoursWorked?: number | null;
}

export interface PayrollPrime {
  code: string;
  label?: string;
  quantity?: number | null;
  rate?: number | null;
  amount?: number | null;
  subjectToContributions?: boolean;
}

export interface PayrollTimesheet {
  id: number;
  candidatId: number;
  candidatFullname?: string;
  sageMatricule?: string | null;
  missionId: number;
  missionRef?: string;
  establishmentName?: string;
  siteName?: string;
  qualification?: string;
  compensation?: number | null;
  compensationType?: 'DAY' | 'HOUR' | 'MONTH' | null;
  baseHourlyRate?: number | null;
  totalHours: number;
  totalDays: number;
  dailyRecordsJson?: string;
  primesJson?: string;
  status: PayrollTimesheetStatus;
  sageTransactionId?: string | null;
  errorMessage?: string | null;
}

export interface Payslip {
  id: number;
  payslipExternalId: string;
  sageMatricule?: string;
  candidatId?: number;
  payrollPeriod: string;
  grossSalary?: number;
  netBeforeTax?: number;
  netToPay?: number;
  taxWithheld?: number;
  netSocial?: number;
  totalEmployerCost?: number;
  daysWorked?: number;
  hoursWorked?: number;
  paymentMethod?: string;
  paymentDate?: string;
}

@Injectable({ providedIn: 'root' })
export class PayrollService {
  private base = `${environment.api.BASE_URL}/api/payroll`;

  constructor(private http: HttpClient) {}

  listPeriods(): Observable<PayrollPeriod[]> {
    return this.http.get<PayrollPeriod[]>(`${this.base}/periods`);
  }

  /** (Re)build the timesheets of a month (yyyy-MM) from mission data. */
  prepare(period: string): Observable<PayrollPeriod> {
    return this.http.post<PayrollPeriod>(`${this.base}/periods/${period}/prepare`, {});
  }

  timesheets(periodId: number): Observable<PayrollTimesheet[]> {
    return this.http.get<PayrollTimesheet[]>(`${this.base}/periods/${periodId}/timesheets`);
  }

  updateTimesheet(timesheetId: number, body: { dailyRecords?: PayrollDayRecord[]; primes?: PayrollPrime[] }): Observable<PayrollTimesheet> {
    return this.http.put<PayrollTimesheet>(`${this.base}/timesheets/${timesheetId}`, body);
  }

  sendToSage(periodId: number): Observable<PayrollPeriod> {
    return this.http.post<PayrollPeriod>(`${this.base}/periods/${periodId}/send`, {});
  }

  refreshPayslips(periodId: number): Observable<Payslip[]> {
    return this.http.post<Payslip[]>(`${this.base}/periods/${periodId}/payslips/refresh`, {});
  }

  payslips(periodId: number): Observable<Payslip[]> {
    return this.http.get<Payslip[]>(`${this.base}/periods/${periodId}/payslips`);
  }

  downloadPayslipPdf(payslipId: number): Observable<Blob> {
    return this.http.get(`${this.base}/payslips/${payslipId}/download`, { responseType: 'blob' });
  }
}
